import { loadJwt, storeJwt, loadClientId, loadUserShares, clearAllMobileAuthData, loadRefreshToken, storeRefreshToken, loadWalletAddress, } from './mobileMpcService';
import { getAuthDiagnostic } from './authDiagnostics';
const STORAGE_KEYS = {
    SESSION_CREATED_AT: 'mobile_session_created_at',
};
export const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const AUTH_REQUEST_TIMEOUT_MS = 10_000;
let ongoingRefreshPromise = null;
let ongoingRefreshTokenAttempt = null;
let refreshGeneration = 0;
const pendingAuthRequests = new Set();
/** Capture before the original API request, not when its delayed 401 arrives.
 * Match the actual request JWT as headers may have been prepared before an await. */
export function captureRefreshBinding(jwt) {
    const wallet = loadWalletAddress();
    const refreshToken = loadRefreshToken();
    if (!jwt || loadJwt() !== jwt || !wallet || !refreshToken)
        return null;
    return {
        normalizedWallet: wallet.toLowerCase(),
        refreshTokenIdentity: refreshToken,
        generation: refreshGeneration,
    };
}
/** Invalidates every in-flight refresh for the previous wallet/session scope. */
export function invalidateRefreshBinding() {
    refreshGeneration++;
    ongoingRefreshTokenAttempt = null;
    for (const cancel of pendingAuthRequests)
        cancel();
    pendingAuthRequests.clear();
}
export function isRefreshBindingCurrent(binding) {
    return binding.generation === refreshGeneration
        && loadWalletAddress()?.toLowerCase() === binding.normalizedWallet
        && loadRefreshToken() === binding.refreshTokenIdentity;
}
/** Capture before async work, including for legacy sessions without a refresh token. */
export function captureAuthInvalidationBinding() {
    const jwt = loadJwt();
    const wallet = loadWalletAddress();
    return jwt && wallet
        ? { jwt, normalizedWallet: wallet.toLowerCase(), generation: refreshGeneration }
        : null;
}
/** Clear only the captured session, then identify that clearing without exposing tokens. */
export function invalidateMobileAuth(binding, reason) {
    if (!binding
        || binding.generation !== refreshGeneration
        || binding.jwt !== loadJwt()
        || binding.normalizedWallet !== loadWalletAddress()?.toLowerCase())
        return;
    clearAllMobileAuthData();
    invalidateRefreshBinding();
    const detail = {
        normalizedWallet: binding.normalizedWallet,
        clearedGeneration: refreshGeneration,
        reason,
    };
    window.dispatchEvent(new CustomEvent('mobile-auth-invalidated', { detail }));
}
/** The clearing's generation survives credential deletion, but not a new session. */
export function isAuthInvalidationCurrent(detail) {
    if (!detail || typeof detail !== 'object')
        return false;
    const event = detail;
    return typeof event.normalizedWallet === 'string'
        && !!event.normalizedWallet
        && Number.isInteger(event.clearedGeneration)
        && event.clearedGeneration === refreshGeneration
        && !loadJwt()
        && !loadWalletAddress()
        && !loadRefreshToken();
}
export function markSessionCreated() {
    try {
        const now = Date.now();
        localStorage.setItem(STORAGE_KEYS.SESSION_CREATED_AT, now.toString());
        console.log('[AuthRefresh] Session creation timestamp stored:', new Date(now).toISOString());
    }
    catch (error) {
        console.error('[AuthRefresh] Failed to mark session created:', getAuthDiagnostic(error));
    }
}
export function clearSessionCreated() {
    localStorage.removeItem(STORAGE_KEYS.SESSION_CREATED_AT);
}
export function isSessionExpired() {
    try {
        const createdAtStr = localStorage.getItem(STORAGE_KEYS.SESSION_CREATED_AT);
        if (!createdAtStr) {
            console.log('[AuthRefresh] No session creation timestamp found');
            return false;
        }
        const createdAt = parseInt(createdAtStr, 10);
        if (isNaN(createdAt)) {
            console.warn('[AuthRefresh] Invalid session creation timestamp');
            return false;
        }
        const now = Date.now();
        const elapsed = now - createdAt;
        const isExpired = elapsed > SESSION_TTL_MS;
        if (isExpired) {
            console.log('[AuthRefresh] Session expired! Elapsed:', elapsed, 'ms, TTL:', SESSION_TTL_MS, 'ms');
        }
        else {
            const daysRemaining = (SESSION_TTL_MS - elapsed) / (24 * 60 * 60 * 1000);
            console.log('[AuthRefresh] Session valid. Days remaining:', daysRemaining.toFixed(2));
        }
        return isExpired;
    }
    catch (error) {
        console.error('[AuthRefresh] Error checking session expiry:', getAuthDiagnostic(error));
        return false;
    }
}
function parseJwt(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        return payload;
    }
    catch (error) {
        console.warn('[AuthRefresh] Failed to parse JWT:', getAuthDiagnostic(error));
        return null;
    }
}
export function isJwtExpired(token) {
    try {
        const payload = parseJwt(token);
        if (!payload || !payload.exp) {
            console.log('[AuthRefresh] JWT has no expiry claim');
            return true;
        }
        const now = Math.floor(Date.now() / 1000);
        const isExpired = payload.exp <= now;
        if (isExpired) {
            console.log('[AuthRefresh] JWT expired');
        }
        return isExpired;
    }
    catch (error) {
        console.error('[AuthRefresh] Error checking JWT expiry:', getAuthDiagnostic(error));
        return true;
    }
}
const baseUrl = process.env.DWINITY_BACKEND ?? "https://api.dev.dwinity.com";
const refreshApiVersion = process.env.API_VERSION || "1.0";
export async function validateStoredAccessToken(expectedWallet) {
    const normalizedWallet = expectedWallet.toLowerCase();
    const storedWallet = loadWalletAddress()?.toLowerCase();
    const jwt = loadJwt();
    if (!jwt)
        return { status: 'no_token' };
    if (!storedWallet || storedWallet !== normalizedWallet)
        return { status: 'wallet_mismatch' };
    const generation = refreshGeneration;
    const isCurrent = () => (generation === refreshGeneration
        && loadWalletAddress()?.toLowerCase() === normalizedWallet
        && loadJwt() === jwt);
    try {
        console.log('[AuthRefresh] Validating stored access token with /users/profile...');
        const response = await withAuthRequestDeadline(signal => fetch(`${baseUrl}/api/${refreshApiVersion}/users/profile`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${jwt}`,
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
            },
            cache: 'no-store',
            signal,
        }));
        if (!isCurrent())
            return { status: 'stale' };
        if (response.ok)
            return { status: 'success', jwt };
        if (response.status === 401 || response.status === 403) {
            return { status: 'rejected', httpStatus: response.status };
        }
        return { status: 'unavailable', httpStatus: response.status };
    }
    catch (error) {
        if (!isCurrent())
            return { status: 'stale' };
        console.warn('[AuthRefresh] Stored access token validation failed:', getAuthDiagnostic(error, '/users/profile'));
        return { status: 'unavailable' };
    }
}
export async function attemptRefreshToken(expectedWallet) {
    const requestedWallet = expectedWallet?.toLowerCase() ?? loadWalletAddress()?.toLowerCase();
    const refreshToken = loadRefreshToken();
    if (!requestedWallet || !refreshToken || (expectedWallet && requestedWallet !== expectedWallet.toLowerCase())) {
        return !refreshToken ? { status: 'no_token' } : { status: 'wallet_mismatch' };
    }
    const binding = {
        normalizedWallet: requestedWallet,
        refreshTokenIdentity: refreshToken,
        generation: refreshGeneration,
    };
    const key = `${binding.normalizedWallet}:${binding.refreshTokenIdentity}:${binding.generation}`;
    if (ongoingRefreshTokenAttempt?.key === key)
        return ongoingRefreshTokenAttempt.promise;
    const attempt = (async () => {
        const stale = () => ({ status: 'stale' });
        try {
            console.log('[AuthRefresh] Trying /auth/refresh with stored refresh token...');
            const { response, data } = await withAuthRequestDeadline(async (signal) => {
                const response = await fetch(`${baseUrl}/api/${refreshApiVersion}/auth/refresh`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${refreshToken}`,
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                    },
                    cache: 'no-store',
                    signal,
                });
                // Keep body parsing inside the deadline; never commit from this operation.
                const data = response.ok ? await response.json() : null;
                return { response, data };
            });
            if (!response.ok) {
                if (!isRefreshBindingCurrent(binding))
                    return stale();
                console.log('[AuthRefresh] /auth/refresh failed:', getAuthDiagnostic({ response: { status: response.status } }, '/auth/refresh'));
                if (response.status === 401 || response.status === 403) {
                    return { status: 'rejected', httpStatus: response.status, binding };
                }
                return { status: 'unavailable', httpStatus: response.status, binding };
            }
            if (!isRefreshBindingCurrent(binding))
                return stale();
            if (data?.authToken) {
                console.log('[AuthRefresh] /auth/refresh succeeded, got new authToken');
                // Commit while the response's old token binding is still current.
                storeJwt(data.authToken);
                if (data.refreshToken) {
                    storeRefreshToken(data.refreshToken);
                    invalidateRefreshBinding();
                }
                return { status: 'success', jwt: data.authToken, binding };
            }
            console.log('[AuthRefresh] /auth/refresh response missing authToken');
            return { status: 'unavailable', httpStatus: response.status, binding };
        }
        catch (error) {
            if (!isRefreshBindingCurrent(binding))
                return stale();
            console.warn('[AuthRefresh] /auth/refresh error:', getAuthDiagnostic(error, '/auth/refresh'));
            return { status: 'unavailable', binding };
        }
    })();
    const trackedAttempt = attempt.finally(() => {
        if (ongoingRefreshTokenAttempt?.promise === trackedAttempt) {
            ongoingRefreshTokenAttempt = null;
        }
    });
    ongoingRefreshTokenAttempt = { key, promise: trackedAttempt };
    return trackedAttempt;
}
/** Typed refresh result for callers that must distinguish revocation from a
 * temporary outage. This intentionally never enters the MPC recovery path. */
export async function attemptAutoRefreshResult() {
    return attemptRefreshToken();
}
async function tryRefreshWithRefreshToken(isCurrentWallet, expectedWallet) {
    const result = await attemptRefreshToken(expectedWallet);
    if (!isCurrentWallet())
        return null;
    return result.status === 'success' ? result.jwt : null;
}
/**
 * Legacy token-or-null wrapper. New code must consume
 * attemptAutoRefreshResult so a transient failure cannot be mistaken for a
 * revoked session.
 */
export async function attemptAutoRefresh() {
    const result = await attemptAutoRefreshResult();
    return result.status === 'success' ? result.jwt : null;
}
export async function tryRefreshOnStartup() {
    console.log('[AuthRefresh] tryRefreshOnStartup called');
    try {
        const currentJwt = loadJwt();
        const clientId = loadClientId();
        const userSharesString = loadUserShares();
        console.log('[AuthRefresh] Startup state:', {
            hasJwt: !!currentJwt,
            hasClientId: !!clientId,
            hasUserShares: !!userSharesString,
        });
        if (!clientId || !userSharesString) {
            console.log('[AuthRefresh] No MPC data found, skipping proactive refresh');
            return null;
        }
        if (isSessionExpired()) {
            console.log('[AuthRefresh] Session TTL has elapsed; preserving credentials until server confirms revocation');
            return null;
        }
        if (!currentJwt) {
            console.log('[AuthRefresh] No JWT found but MPC data exists and session is valid, attempting refresh');
            return attemptAutoRefresh();
        }
        if (isJwtExpired(currentJwt)) {
            console.log('[AuthRefresh] JWT is expired, attempting refresh');
            return attemptAutoRefresh();
        }
        console.log('[AuthRefresh] JWT looks valid client-side, proactively refreshing to ensure server validity');
        try {
            const freshJwt = await attemptAutoRefresh();
            if (freshJwt) {
                console.log('[AuthRefresh] Proactive refresh succeeded, using fresh JWT');
                return freshJwt;
            }
            console.log('[AuthRefresh] Proactive refresh returned null but JWT is still valid client-side — keeping existing JWT');
            return currentJwt;
        }
        catch (error) {
            console.warn('[AuthRefresh] Proactive refresh failed but JWT is still valid client-side — keeping existing JWT:', getAuthDiagnostic(error, '/auth/refresh'));
            return currentJwt;
        }
    }
    catch (error) {
        console.error('[AuthRefresh] Error in tryRefreshOnStartup:', getAuthDiagnostic(error));
        return null;
    }
}
/**
 * Bound both fetch and response-body reading, even if a transport ignores abort.
 * The operation must only read data: storage/state commits belong after the race.
 */
async function withAuthRequestDeadline(operation) {
    const controller = new AbortController();
    let cancel;
    const cancelled = new Promise((_, reject) => {
        cancel = () => {
            reject(new Error('Auth request cancelled or timed out'));
            controller.abort();
        };
    });
    pendingAuthRequests.add(cancel);
    const timer = setTimeout(cancel, AUTH_REQUEST_TIMEOUT_MS);
    try {
        return await Promise.race([operation(controller.signal), cancelled]);
    }
    finally {
        clearTimeout(timer);
        pendingAuthRequests.delete(cancel);
    }
}
